import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CheckCircleRounded,
  RadioButtonUncheckedRounded,
  ArrowForwardRounded,
  PaletteRounded,
  PublicRounded,
  SchoolRounded,
  AssignmentRounded,
  GroupsRounded,
  PlayCircleRounded,
  RocketLaunchRounded,
} from '@mui/icons-material';
import { useSetupProgress, type SetupStage } from '../../hooks/use-setup-progress';

// Stage → icon (header accents only).
const STAGE_ICON: Record<string, JSX.Element> = {
  identity: <PaletteRounded />,
  site: <PublicRounded />,
  academic: <SchoolRounded />,
  admissions: <AssignmentRounded />,
  team: <GroupsRounded />,
  operations: <PlayCircleRounded />,
};

// "Explore the platform" cards → real routes.
const EXPLORE: { key: string; href: string }[] = [
  { key: 'people', href: '/people' },
  { key: 'crm', href: '/crm/contacts' },
  { key: 'admissions', href: '/admissions' },
  { key: 'sections', href: '/sections' },
  { key: 'lms', href: '/curso' },
  { key: 'assignments', href: '/assignments' },
  { key: 'grades', href: '/grades' },
  { key: 'announcements', href: '/academic/anuncios' },
  { key: 'tuition', href: '/tuition' },
  { key: 'scholarships', href: '/scholarships' },
  { key: 'academic', href: '/academic' },
  { key: 'reports', href: '/reportes' },
  { key: 'settings', href: '/settings' },
];

export function SetupCenterPage() {
  const { t } = useTranslation('setup');
  const { t: tCap } = useTranslation('cap');
  const navigate = useNavigate();
  const theme = useTheme();
  const { loading, pct, doneCount, totalCount, stages, nextHref } = useSetupProgress({
    fresh: true,
  });

  const allDone = !loading && pct === 100;

  return (
    <Box sx={{ maxWidth: 920, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* ── Header + progress ── */}
      <Typography variant="h4" fontWeight={800} gutterBottom>
        {t('title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={3}>
        {t('subtitle')}
      </Typography>

      <Card sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          {loading ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={22} />
              <Typography color="text.secondary">{t('loading')}</Typography>
            </Stack>
          ) : allDone ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <CheckCircleRounded sx={{ color: 'success.main', fontSize: 40 }} />
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {t('allDoneTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('allDoneBody')}
                </Typography>
              </Box>
            </Stack>
          ) : (
            <>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-end"
                mb={1}
                flexWrap="wrap"
                gap={1}
              >
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    {t('essentialProgress')}
                  </Typography>
                  <Typography variant="h4" fontWeight={800} lineHeight={1}>
                    {t('pctComplete', { pct })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    {t('stepsDone', { done: doneCount, total: totalCount })}
                  </Typography>
                </Box>
                {nextHref && (
                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardRounded />}
                    onClick={() => navigate(nextHref)}
                  >
                    {t('continue')}
                  </Button>
                )}
              </Stack>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{ height: 8, borderRadius: 4, mt: 1.5 }}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Checklist ── */}
      <Stack spacing={2.5}>
        {stages.map((stage) => (
          <StageCard key={stage.key} stage={stage} onGo={(href) => navigate(href)} />
        ))}
      </Stack>

      {/* ── CAP gateway: the next horizon after the seminary is set up ── */}
      <Card
        sx={{
          mt: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: '#fff',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <RocketLaunchRounded sx={{ fontSize: 36 }} />
              <Box>
                <Typography variant="h6" fontWeight={800}>
                  {tCap('journey.gateway.title')}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.92, maxWidth: 560 }}>
                  {tCap('journey.gateway.body')}
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="contained"
              endIcon={<ArrowForwardRounded />}
              onClick={() => navigate('/academic?tab=programs')}
              sx={{
                bgcolor: '#fff',
                color: '#5b21b6',
                fontWeight: 700,
                textTransform: 'none',
                flexShrink: 0,
                '&:hover': { bgcolor: '#f3f0ff' },
              }}
            >
              {tCap('journey.gateway.cta')}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* ── Explore the platform ── */}
      <Box mt={6}>
        <Typography variant="h5" fontWeight={800} gutterBottom>
          {t('explore.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2.5}>
          {t('explore.subtitle')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          }}
        >
          {EXPLORE.map(({ key, href }) => (
            <Card
              key={key}
              onClick={() => navigate(href)}
              sx={{
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'border-color .15s, transform .15s',
                border: `1px solid ${theme.palette.divider}`,
                '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)' },
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Typography fontWeight={700} gutterBottom>
                  {t(`explore.items.${key}.title`)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(`explore.items.${key}.desc`)}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

// ─── Stage card ───────────────────────────────────────────────────────────────

function StageCard({ stage, onGo }: { stage: SetupStage; onGo: (href: string) => void }) {
  const { t } = useTranslation('setup');
  const essentialDone = stage.steps.filter((s) => !s.optional && s.done).length;
  const essentialTotal = stage.steps.filter((s) => !s.optional).length;

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" mb={0.5}>
          <Box sx={{ color: 'primary.main', display: 'flex' }}>{STAGE_ICON[stage.key]}</Box>
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
            {t(`stages.${stage.key}`)}
          </Typography>
          {essentialTotal > 0 && (
            <Typography variant="caption" color="text.secondary">
              {essentialDone}/{essentialTotal}
            </Typography>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t(`stages.${stage.key}Hint`)}
        </Typography>

        <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
          {stage.steps.map((step) => (
            <Stack
              key={step.key}
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ py: 1.25 }}
            >
              {step.done ? (
                <CheckCircleRounded sx={{ color: 'success.main' }} />
              ) : (
                <RadioButtonUncheckedRounded sx={{ color: 'text.disabled' }} />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography fontWeight={600} sx={{ textDecoration: step.done ? 'none' : 'none' }}>
                    {t(`steps.${step.key}.title`)}
                  </Typography>
                  {step.optional && (
                    <Chip label={t('status.optional')} size="small" variant="outlined" />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {t(`steps.${step.key}.why`)}
                </Typography>
              </Box>
              {step.done ? (
                <Chip label={t('status.done')} size="small" color="success" variant="outlined" />
              ) : (
                <Button size="small" variant="text" onClick={() => onGo(step.href)}>
                  {t('go')}
                </Button>
              )}
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
